import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { KpiQueryParams } from '../../types/kpi.types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Loader } from '../Loader';

interface NeverTransferProbabilityProps {
  queryParams: KpiQueryParams;
}

interface ProbabilityData {
  daysAfterRegistration: number;
  usersNotTransferredByDayX: number;
  usersNeverTransferred: number;
  usersEventuallyTransferred: number;
  neverTransferProbability: number;
}

interface SecondTransferProbabilityData {
  daysAfterFirstTransfer: number;
  usersNotMadeSecondByDayX: number;
  usersNeverMadeSecond: number;
  usersEventuallyMadeSecond: number;
  neverSecondTransferProbability: number;
}

interface ThirdTransferProbabilityData {
  daysAfterSecondTransfer: number;
  usersNotMadeThirdByDayX: number;
  usersNeverMadeThird: number;
  usersEventuallyMadeThird: number;
  neverThirdTransferProbability: number;
}

const NeverTransferProbability: React.FC<NeverTransferProbabilityProps> = ({
  queryParams,
}) => {
  const [data, setData] = useState<ProbabilityData[]>([]);
  const [secondTransferData, setSecondTransferData] = useState<SecondTransferProbabilityData[]>([]);
  const [thirdTransferData, setThirdTransferData] = useState<ThirdTransferProbabilityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [neverTransfer, neverSecond, neverThird] = await Promise.all([
          analyticsService.getNeverTransferProbability({
            database: queryParams.database,
            companyId: queryParams.companyId,
          }),
          analyticsService.getNeverSecondTransferProbability({
            database: queryParams.database,
            companyId: queryParams.companyId,
          }),
          analyticsService.getNeverThirdTransferProbability({
            database: queryParams.database,
            companyId: queryParams.companyId,
          }),
        ]);
        setData(neverTransfer);
        setSecondTransferData(neverSecond);
        setThirdTransferData(neverThird);
      } catch (err: any) {
        setError(err.message || 'Failed to load probability data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [queryParams.database, queryParams.companyId]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#d32f2f' }}>
        Error: {error}
      </div>
    );
  }

  if (data.length === 0 && secondTransferData.length === 0 && thirdTransferData.length === 0) {
    return (
      <div style={{ padding: '20px', color: '#666' }}>
        No data available for analysis.
      </div>
    );
  }

  // Prepare data for chart
  const chartData = data.map(row => ({
    days: row.daysAfterRegistration,
    probability: row.neverTransferProbability,
    usersNotTransferred: row.usersNotTransferredByDayX,
    neverTransferred: row.usersNeverTransferred,
    eventuallyTransferred: row.usersEventuallyTransferred,
  }));

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: '24px',
      }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600, color: '#333' }}>
          Probability of Never Transferring
        </h2>
        <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '14px' }}>
          For each day X after registration, shows the probability that a user will never transfer, 
          given they haven't transferred by day X. Analysis includes only users registered at least 90 days ago 
          to ensure accurate final outcomes. Aggregated across all companies.
        </p>

        {/* Chart 1: Never Transferring */}
        <div style={{ marginBottom: '40px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Probability of Never Transferring (First Transfer)
          </h3>
          <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '13px' }}>
            For each day X after registration, shows the probability that a user will never transfer, 
            given they haven't transferred by day X.
          </p>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="days" 
                label={{ value: 'Days After Registration', position: 'insideBottom', offset: -5 }}
                stroke="#666"
              />
              <YAxis 
                label={{ value: 'Probability (%)', angle: -90, position: 'insideLeft' }}
                domain={[0, 100]}
                stroke="#666"
              />
              <Tooltip 
                formatter={(value: any, name: any) => {
                  if (name === 'probability') return [`${Number(value).toFixed(1)}%`, 'Never Transfer Probability'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Day ${label}`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="probability" 
                stroke="#d32f2f" 
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Never Transfer Probability (%)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 2: Never Making Second Transfer */}
        {secondTransferData.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
              Probability of Never Making Second Transfer
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '13px' }}>
              For each day X after their first transfer, shows the probability that a user will never make a second transfer, 
              given they haven't made it by day X. Only includes users who made their first transfer at least 90 days ago.
            </p>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart 
                data={secondTransferData.map(row => ({
                  days: row.daysAfterFirstTransfer,
                  probability: row.neverSecondTransferProbability,
                }))} 
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="days" 
                  label={{ value: 'Days After First Transfer', position: 'insideBottom', offset: -5 }}
                  stroke="#666"
                />
                <YAxis 
                  label={{ value: 'Probability (%)', angle: -90, position: 'insideLeft' }}
                  domain={[0, 100]}
                  stroke="#666"
                />
                <Tooltip 
                  formatter={(value: any) => [`${Number(value).toFixed(1)}%`, 'Never Second Transfer Probability']}
                  labelFormatter={(label) => `Day ${label}`}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="probability" 
                  stroke="#ff6b6b" 
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  name="Never Second Transfer Probability (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Chart 3: Never Making Third Transfer */}
        {thirdTransferData.length > 0 && (
          <div style={{ marginBottom: '40px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
              Probability of Never Making Third Transfer
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '13px' }}>
              For each day X after their second transfer, shows the probability that a user will never make a third transfer, 
              given they haven't made it by day X. Only includes users who made their second transfer at least 90 days ago.
            </p>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart 
                data={thirdTransferData.map(row => ({
                  days: row.daysAfterSecondTransfer,
                  probability: row.neverThirdTransferProbability,
                }))} 
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="days" 
                  label={{ value: 'Days After Second Transfer', position: 'insideBottom', offset: -5 }}
                  stroke="#666"
                />
                <YAxis 
                  label={{ value: 'Probability (%)', angle: -90, position: 'insideLeft' }}
                  domain={[0, 100]}
                  stroke="#666"
                />
                <Tooltip 
                  formatter={(value: any) => [`${Number(value).toFixed(1)}%`, 'Never Third Transfer Probability']}
                  labelFormatter={(label) => `Day ${label}`}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="probability" 
                  stroke="#ff9800" 
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  name="Never Third Transfer Probability (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Detailed Table
          </h3>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                  Days After<br/>Registration (X)
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                  Users Not<br/>Transferred by Day X
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee' }}>
                  Never<br/>Transferred
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#e8f5e9' }}>
                  Eventually<br/>Transferred
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', backgroundColor: '#fff3e0' }}>
                  Probability of<br/>Never Transferring (%)
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr
                  key={row.daysAfterRegistration}
                  style={{
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa',
                  }}
                >
                  <td style={{ padding: '12px', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                    {row.daysAfterRegistration}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6' }}>
                    {row.usersNotTransferredByDayX.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee' }}>
                    {row.usersNeverTransferred.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#e8f5e9' }}>
                    {row.usersEventuallyTransferred.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', backgroundColor: '#fff3e0', fontWeight: 600, fontSize: '14px' }}>
                    {row.neverTransferProbability.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#333' }}>
            📊 How to Read This Analysis
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', fontSize: '13px', lineHeight: '1.6' }}>
            <li><strong>Days After Registration (X):</strong> Number of days that have passed since user registration</li>
            <li><strong>Users Not Transferred by Day X:</strong> Total users who hadn't made their first transfer by day X (and were registered at least 90 days ago)</li>
            <li><strong>Never Transferred:</strong> Users who never made a transfer (still at 0 transfers)</li>
            <li><strong>Eventually Transferred:</strong> Users who made their first transfer after day X</li>
            <li><strong>Probability of Never Transferring:</strong> Percentage of users (who hadn't transferred by day X) who never transferred. This is the key metric - it shows how likely a user is to never transfer if they haven't done so by day X.</li>
          </ul>
          <p style={{ margin: '12px 0 0 0', color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
            💡 <strong>Key Insight:</strong> As days pass without a transfer, the probability of never transferring increases. 
            This helps identify critical intervention points - if a user hasn't transferred by day X, what's their chance of never transferring?
          </p>
        </div>
      </div>
    </div>
  );
};

export default NeverTransferProbability;

